import React, { useEffect, useMemo, useState } from 'react';

import { useQuery, useQueryClient } from '@tanstack/react-query';
import { mountRootParcel } from 'single-spa';
import Parcel from 'single-spa-react/parcel';

import { createReactRenderer } from 'helpers/react-renderer';
import { InvoicingConfig } from 'root/apps/QuoteMachineIntegration/common';
import { useAggregateSale } from 'root/apps/QuoteMachineIntegration/hooks/useAggregateSale';
import { useAggregateWorkorder } from 'root/apps/QuoteMachineIntegration/hooks/useAggregateWorkorder';
import CustomerAccountService from 'root/apps/QuoteMachineIntegration/services/CustomerAccountService';
import PaymentRequestService from 'root/apps/QuoteMachineIntegration/services/PaymentRequestService';
import { AggregateSaleData } from 'root/apps/QuoteMachineIntegration/state/AggregateSaleData';
import { PaymentRequestFormData } from 'root/apps/QuoteMachineIntegration/state/PaymentRequestFormData';
import { PaymentRequestModalProps } from 'root/apps/QuoteMachineIntegration/state/PaymentRequestModalProps';
import { SystemJS } from 'root/apps/QuoteMachineIntegration/SystemJS';
import { toCents } from 'root/apps/QuoteMachineIntegration/utils/PaymentRequestBuilders';
import { getProposalPreviewURL as getDocumentURL } from 'root/apps/QuoteMachineIntegration/utils/ProposalPreviewConfig';
import { getSessionData } from 'root/apps/QuoteMachineIntegration/utils/SessionData';
import { datadogRum } from 'root/datadog/datadogRum';

export function PaymentRequestModal({
  saleID,
  workorderID,
  quoteID,
  onSubmit,
  handleCancel,
}: PaymentRequestModalProps) {
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(true);
  const [documentURL] = useState(getDocumentURL);
  const [customerEmail, setCustomerEmail] = useState<string>('');

  // Determine context (sale | workorder | quote). Discriminator rules:
  //   - active sale → only `saleID` is set
  //   - workorder   → only `workorderID` is set
  //   - quote       → BOTH `saleID` (transaction_id, used to fetch sale data) AND
  //                   `quoteID` (invoice_id / quote primary key, used for display) are set
  // The quote builder uses `saleID` to build the documentRef (`quote-{saleID}`) and
  // `quoteID` for the pricing-table title ("Quote #{quoteID}").
  const context = useMemo(() => {
    const configs = {
      workorder: {
        type: 'workorder',
        idField: 'workorderID',
        id: workorderID,
        buildDto: PaymentRequestService.buildPaymentRequestDtoForWorkorder,
      },
      quote: {
        type: 'quote',
        idField: 'quoteID',
        id: saleID, // sale endpoint key — quote data is fetched via the linked sale
        buildDto: (data: AggregateSaleData) =>
          PaymentRequestService.buildPaymentRequestDtoForQuote(data, quoteID),
      },
      sale: {
        type: 'sale',
        idField: 'saleID',
        id: saleID,
        buildDto: PaymentRequestService.buildPaymentRequestDto,
      },
    } as const;

    let key: 'workorder' | 'quote' | 'sale' = 'sale';
    if (workorderID && !saleID && !quoteID) {
      key = 'workorder';
    } else if (quoteID && saleID && !workorderID) {
      key = 'quote';
    }

    return {
      ...configs[key],
      isWorkorder: key === 'workorder',
      isQuote: key === 'quote',
    } as const;
  }, [workorderID, saleID, quoteID]);

  // load data based on context. Quote uses the sale endpoint since a saved quote is a sale row.
  const { aggregateSale, loading: loadingSale } = useAggregateSale(
    context.isWorkorder ? undefined : context.id
  );
  const { aggregateWorkorder, loading: loadingWorkorder } = useAggregateWorkorder(workorderID);

  // Use the appropriate data based on context
  const aggregateData = useMemo(
    () => (context.isWorkorder ? aggregateWorkorder : aggregateSale),
    [context.isWorkorder, aggregateWorkorder, aggregateSale]
  );
  const loading = context.isWorkorder ? loadingWorkorder : loadingSale;

  // Memoize query key to prevent unnecessary re-fetches
  const queryKey = useMemo(
    () => ['paymentRequestDto', context.id, context.type],
    [context.id, context.type]
  );

  // build the initial payment request dto to send to the mfe
  const {
    data: paymentRequestDto,
    isLoading: creatingPaymentRequest,
    isError: errorCreatingPaymentRequest,
    error: errorObj,
  } = useQuery({
    queryKey,
    queryFn: () => context.buildDto(aggregateData as AggregateSaleData),
    enabled: !!aggregateData && !loading,
    retry: false,
    staleTime: 0,
    refetchOnMount: 'always',
    refetchOnReconnect: true,
    refetchOnWindowFocus: false,
  });

  // log error in datadog if building the payment request dto fails
  useEffect(() => {
    if (errorCreatingPaymentRequest && errorObj) {
      const e = errorObj instanceof Error ? errorObj : new Error(String(errorObj));
      datadogRum.addError(e, {
        message: `Failed to build payment request dto for ${context.idField}`,
        [context.idField]: context.id,
      });
    }
  }, [errorCreatingPaymentRequest, errorObj, context.id, context.idField]);

  const accountRef = paymentRequestDto?.accountRef ?? null;
  const pricingTables = paymentRequestDto?.pricingTables ?? null;

  const working = useMemo(
    () => loading || creatingPaymentRequest,
    [loading, creatingPaymentRequest]
  );

  // set customer email
  useEffect(() => {
    if (!customerEmail && paymentRequestDto?.sendToEmail) {
      setCustomerEmail(paymentRequestDto.sendToEmail);
    }
  }, [paymentRequestDto, customerEmail]);

  // handle "working" spinner visibility
  useEffect(() => {
    if (working) {
      window.showNotifier?.('working');
    } else {
      window.hideNotifier?.('working');
    }
    return () => {
      window.hideNotifier?.('working');
    };
  }, [working]);

  // ready to preview if accountRef exists and there are pricing tables
  const previewReady = useMemo(() => {
    return !!accountRef && Array.isArray(pricingTables) && pricingTables.length > 0;
  }, [accountRef, pricingTables]);

  // all required data is loaded, no requests in progress, and preview is prepared
  const ready = useMemo(() => {
    return !!aggregateData && !creatingPaymentRequest && !loading && previewReady;
  }, [aggregateData, creatingPaymentRequest, loading, previewReady]);

  async function handleSubmit(payload: PaymentRequestFormData) {
    const { option, paymentRequestDto: editedDto } = payload;

    // requestedAmount entered on the modal (data coming from Invoicing MFE)
    const formattedRequestedAmount = toCents(editedDto?.requestedAmount ?? 0);
    if (formattedRequestedAmount <= 0) {
      window.showNotifier('warning', 'Enter a valid amount greater than 0.');
      return;
    }

    // make sure we only send the latest amount value
    const newPaymentRequestDto = { ...editedDto, requestedAmount: formattedRequestedAmount };

    const hasCreditAccount =
      !!newPaymentRequestDto.customer.creditAccountRef &&
      newPaymentRequestDto.customer.creditAccountRef !== '0';

    try {
      window.showNotifier('working');

      if (!hasCreditAccount) {
        const customerAccount = await CustomerAccountService.createOrUpdateCustomerCreditAccount(
          payload.paymentRequestDto.customer.customerRef,
          '0' // defaults credit limit to 0
        );
        newPaymentRequestDto.customer.creditAccountRef = customerAccount.creditAccountRef;
      }

      const response = await PaymentRequestService.createPaymentRequest(newPaymentRequestDto);
      const proposalUrl = response?.data?.proposalUrl;

      if (!newPaymentRequestDto.sendToEmail && proposalUrl) {
        onSubmit(option);
        // eslint-disable-next-line consistent-return
        return { proposalUrl };
      }

      // Invalidate payment request totals query to refresh the amount on the register
      queryClient.invalidateQueries({ queryKey: ['paymentRequestTotals'] });
      onSubmit(option);
    } catch (err) {
      datadogRum.addError(err as Error, {
        message: 'Failed to create payment request',
        [context.idField]: context.id,
      });
      window.showNotifier('warning', 'Failed to create payment request');
    } finally {
      window.hideNotifier('working');
    }
  }

  return ready ? (
    <Parcel
      key="payment-request-modal-with-sidebar"
      mountParcel={mountRootParcel}
      baseLocation="/register.php"
      skipCSSUnmount={true}
      open={open}
      setOpen={setOpen}
      customerEmail={customerEmail}
      setCustomerEmail={setCustomerEmail}
      documentURL={documentURL}
      handleCancel={handleCancel}
      paymentRequestDto={paymentRequestDto}
      onCancel={() => {
        setOpen(false);
        handleCancel();
      }}
      onSubmit={handleSubmit}
      config={async () =>
        (await SystemJS.import<InvoicingConfig>('@lightspeed/invoicing')).default
          .PaymentRequestModalConfig
      }
      handleError={(err: Error) => {
        datadogRum.addError(err, {
          message: 'Payment Request modal error',
          [context.idField]: context.id,
        });
        setOpen(false);
        handleCancel();
      }}
      showShareableLink={true}
      showItemsToggle={!context.isQuote}
      sessionData={getSessionData()}
    />
  ) : null;
}

export const renderPaymentRequestModal = (props: PaymentRequestModalProps) => {
  const RenderedComponent = createReactRenderer({
    component: PaymentRequestModal,
    selector: '[data-react-root="ReactModal"]',
  });

  RenderedComponent.mount({
    props: { ...props },
  });
};
